import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRewardPayOutSchema } from "@features/Referral/components/ReferralEarnings/RewardPayOutModal/useRewardPayOutSchema";
import { Button } from "@components/Button/Button";
import { BanknotesIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { usePayOutReferralsRewardMutation } from "@services/referrals/mutations";
import { useReferralsStatusQuery } from "@services/referrals/queries";
import { isNullOrUndefined } from "@utils/common";
import { formatPrice } from "@utils/units";

interface IFormData {
  name: string;
  bankAccountNumber: string;
}

export const RewardPayOutModal: React.FC<IUseModalProps> = (props) => {
  const { t } = useTranslation();
  const modal = useModal(props);
  const schema = useRewardPayOutSchema();
  const { data: status } = useReferralsStatusQuery();
  const { handleSubmit, control, setError } = useForm<IFormData>({
    resolver: zodResolver(schema),
  });
  const { setApiErrors } = useApiErrorUtils(setError);

  const amount = status?.referralReward?.pendingPayment ?? 0;

  const { mutateAsync: payOutReward, isPending } =
    usePayOutReferralsRewardMutation();

  const payOut = async (data: IFormData) => {
    const response = await payOutReward(data).catch(setApiErrors);

    if (!isNullOrUndefined(response)) {
      modal.close();
      toast.success(t("referral.payOutModal.toast.success"));
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: BanknotesIcon }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("referral.payOutModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(payOut)}
            className="w-full"
            isLoading={isPending}
            disabled={!(amount > 0)}
          >
            {t("referral.payOutModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("referral.payOutModal.title")}
    >
      <form className="flex flex-col gap-4">
        <FormInput
          control={control}
          name="name"
          label={t("referral.payOutModal.form.fields.name.label")}
        />
        <FormInput
          control={control}
          name="bankAccountNumber"
          label={t("referral.payOutModal.form.fields.bankAccountNumber.label")}
        />
        <div>
          {t("referral.payOutModal.form.amount")}
          {": "}
          <span className="font-semibold">{formatPrice(amount)}</span>
        </div>
      </form>
    </Modal>
  );
};
